import { __awaiter, __generator, __read } from "tslib";
import { getCustomPivotFromSettingKey, isCustomPivotSettingKey, LayoutPreferenceSettingKey, useSingletonEdgeworthPivot, } from '@office-start/base-edgeworth-control';
import { useCapabilities } from '@office-start/capabilities-service';
import { useCommandFilter } from '@office-start/command-store';
import { useOfficeComponentContext } from '@office-start/component-context-service';
import DialogControl from '@office-start/dialog-control';
import { useEdgeworthDataPrep, useEdgeworthInstrumentation, useEditLinkDialog, } from '@office-start/edgeworth-component';
import { useEdgeworthSettings, useMutateEdgeworthSetting, useMutateSettingsCallbacks, } from '@office-start/edgeworth-service';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { fetchPersonPictureData } from '@office-start/graph-service';
import { useIdentity } from '@office-start/identity-service';
import { logClickEvent, logImpressionEvent, } from '@office-start/instrumentation-service';
import { KnownInstrumentationEvents, } from '@office-start/instrumentation-types';
import { getLaunchInstrumentationParamsFromKnownArea, launchUrl, } from '@office-start/launcher-service';
import { LayoutOption } from '@office-start/layout-switch-control';
import { useLocStrings } from '@office-start/loc-service';
import { OfficeRouteItemType, OfficeWorkload, } from '@office-start/officehome-types';
import { usePeopleCard } from '@office-start/people-card-service';
import QuickAccessControl from '@office-start/quick-access-control';
import { IDLE_VERYLOW_PRIORITY, scheduleWork, } from '@office-start/scheduler-service';
import { useRegisterTeachingUiTargetElement } from '@office-start/teaching-ui-service';
import { logImpression, logUserAction } from '@office-start/telemetry-service';
import { useIsDarkMode } from '@office-start/theming-service';
import UploadButtonControl from '@office-start/upload-button-control';
import { useUploadCallback, useUploadErrorDialog, } from '@office-start/upload-service';
import { useVisualBackgroundEnabled } from '@office-start/visual-service';
import { useProvisionedWorkloads } from '@office-start/workloads-service';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getFilteredCustomPivotKey } from './getFilteredCustomPivotKey';
import { useDeleteCustomPivot } from './hooks/useDeleteCustomPivot';
import { useGetOrCreateUpnNameMap } from './hooks/useGetOrCreateUpnNameMap';
import { useQuickAccessCallbacks } from './hooks/useQuickAccessCallbacks';
export var QuickAccessComponent = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h;
    var bridgeData = _a.bridgeData;
    var context = useOfficeComponentContext().data;
    var locStrings = useLocStrings().data;
    var identity = useIdentity().data;
    var capabilities = useCapabilities().data;
    var featureGates = useFeatureGates().data;
    var darkMode = useIsDarkMode();
    var _j = __read(useEdgeworthSettings(), 2), settings = _j[0], setSettings = _j[1];
    var _k = useSingletonEdgeworthPivot(), stickyPivot = _k.stickyPivot, setStickyPivot = _k.setStickyPivot;
    var deleteCustomPivot = useDeleteCustomPivot();
    var PeopleCard = usePeopleCard();
    var allWorkloads = useProvisionedWorkloads();
    var workloadColorMap = useMemo(function () {
        var workloadColorMap = new Map();
        allWorkloads === null || allWorkloads === void 0 ? void 0 : allWorkloads.filter(function (item) { return !!(item === null || item === void 0 ? void 0 : item.FileType); }).map(function (workload) {
            workload.FileType &&
                workloadColorMap.set(workload.FileType, workload.Color);
        });
        return workloadColorMap;
    }, [allWorkloads]);
    var isCustomPivotsEnabled = useMemo(function () { return capabilities === null || capabilities === void 0 ? void 0 : capabilities.quickAccessV1CustomPivotEnabled; }, [capabilities === null || capabilities === void 0 ? void 0 : capabilities.quickAccessV1CustomPivotEnabled]);
    var gridViewEnabled = useMemo(function () { return capabilities === null || capabilities === void 0 ? void 0 : capabilities.quickAccessV1GridViewEnabled; }, [capabilities === null || capabilities === void 0 ? void 0 : capabilities.quickAccessV1GridViewEnabled]);
    var isVisualRefresh = useVisualBackgroundEnabled();
    var currentLayout = useMemo(function () {
        if (gridViewEnabled && settings && settings.length) {
            var layoutSetting = settings.find(function (setting) { return setting.name === LayoutPreferenceSettingKey; });
            if (layoutSetting) {
                return LayoutOption[layoutSetting.value];
            }
        }
        // Default to list layout
        return LayoutOption.List;
    }, [gridViewEnabled, settings]);
    var navigateToMyContent = useCallback(function (newPivot) {
        if (!context) {
            return;
        }
        var route = {
            route: 'MyContent',
            type: OfficeRouteItemType.Base,
        };
        var knownArea = 'QuickAccess';
        var elementId = 'office_mycontent';
        var param = getLaunchInstrumentationParamsFromKnownArea(knownArea, undefined, elementId);
        if (stickyPivot !== newPivot) {
            // need to check for only default pivots
            // TODO - Stickiness for custom pivots.
            switch (newPivot) {
                case 'recent':
                case 'shared':
                case 'favorites':
                    setStickyPivot(newPivot);
                    break;
                default:
                    setStickyPivot('all');
            }
        }
        var action = 'OpenDocument';
        launchUrl(route, { action: action }, param);
        logClickEvent(KnownInstrumentationEvents.MyContentLinkClick, elementId, knownArea);
        logUserAction({
            eventName: KnownInstrumentationEvents.MyContentLinkClick,
            id: elementId,
            area: knownArea,
            result: 'Navigate',
            target: 'MyContentLink',
        });
    }, [context, setStickyPivot, stickyPivot]);
    var _l = useEdgeworthDataPrep({
        instrumentationArea: 'QuickAccess',
        bridgeData: bridgeData,
        context: context,
        locStrings: locStrings,
        identity: identity,
    }), documents = _l.documents, isError = _l.isError, isInitialStable = _l.isInitialStable, callbacks = _l.callbacks, tags = _l.tags, msaUserHint = _l.msaUserHint;
    var customPivots = useMemo(function () {
        if (!isCustomPivotsEnabled || !settings || !settings.length) {
            return undefined;
        }
        return settings
            .filter(function (setting) { return isCustomPivotSettingKey(setting.name); })
            .map(function (setting) {
            return getCustomPivotFromSettingKey(setting.name, tags === null || tags === void 0 ? void 0 : tags.tagIdToItemsDocumentIdsMap);
        })
            .reverse() // Newer settings show up first in the list
            .filter(function (pivot) { return !!pivot; });
    }, [isCustomPivotsEnabled, settings, tags]);
    var registerFavoriteTabElement = useRegisterTeachingUiTargetElement('Edgeworth_Favorite');
    var registerUploadButtonElement = useRegisterTeachingUiTargetElement('Edgeworth_UploadButton');
    var onPivotRefChange = useCallback(function (id, element) {
        if (id === 'favorites') {
            registerFavoriteTabElement(element);
        }
    }, [registerFavoriteTabElement]);
    var _m = useMutateSettingsCallbacks(settings, setSettings), onUpsertSetting = _m.onUpsertSetting, onDeleteSetting = _m.onDeleteSetting, onUpsertSettingError = _m.onUpsertSettingError, onDeleteSettingError = _m.onDeleteSettingError;
    var _o = useMutateEdgeworthSetting(onUpsertSetting, onDeleteSetting, onUpsertSettingError, onDeleteSettingError), upsertMutation = _o.upsertMutation, deleteMutation = _o.deleteMutation;
    var quickAccessCallbacks = useQuickAccessCallbacks({
        edgeworthCallbacks: callbacks,
        upsertMutation: upsertMutation,
        deleteMutation: deleteMutation,
    });
    //TODO remove check for consumer new user once loading UX is in place
    var canRender = !!((((identity === null || identity === void 0 ? void 0 : identity.isMsa) && documents.length === 0) ||
        documents.length ||
        isInitialStable ||
        isError) &&
        identity &&
        locStrings &&
        context);
    var editLinkDialog = useEditLinkDialog('QuickAccess');
    var _p = __read(useState(false), 2), showDialog = _p[0], setShowDialog = _p[1];
    var changeSetShowDialog = useCallback(function (showDialog) {
        setShowDialog(showDialog);
    }, []);
    var _q = useUploadCallback('QuickAccess'), v1UploadStatus = _q.v1UploadStatus, openFileSelector = _q.openFileSelector, resetV1UploadStatus = _q.resetV1UploadStatus;
    var _r = useUploadErrorDialog(v1UploadStatus, changeSetShowDialog, locStrings), dialogText = _r.dialogText, dialogTitle = _r.dialogTitle;
    var uploadButton = useMemo(function () {
        return locStrings ? (React.createElement(UploadButtonControl, { uploadActionTitle: locStrings.edgeworthResources.uploadActionTitle, ref: registerUploadButtonElement, buttonId: "upload-button-QuickAccess", onButtonClick: function () { return openFileSelector(); } })) : undefined;
    }, [locStrings, openFileSelector, registerUploadButtonElement]);
    useEdgeworthInstrumentation({
        documents: documents,
        bridgeData: bridgeData,
        canRender: canRender,
        isError: isError,
        isInitialStable: isInitialStable,
        paintMarker: 'Paint_QuickAccess',
        instrumentationArea: 'QuickAccess',
    });
    var getOrCreateUpnNameMap = useGetOrCreateUpnNameMap(documents);
    var fetchPictureData = useCallback(function (upn) { return __awaiter(void 0, void 0, void 0, function () {
        var personPictureData, e_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, fetchPersonPictureData(upn)];
                case 1:
                    personPictureData = (_a.sent()).result;
                    return [2 /*return*/, personPictureData];
                case 2:
                    e_1 = _a.sent();
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/, undefined];
            }
        });
    }); }, []);
    useEffect(function () {
        if (!customPivots) {
            return;
        }
        scheduleWork(function () {
            var _a;
            var distribution = customPivots.reduce(function (combined, current, index) {
                var pivotType = getFilteredCustomPivotKey(current.customFilterType, current.customFilterKey);
                return "".concat(combined).concat(index === 0 ? '' : ';').concat(pivotType);
            }, '');
            logImpressionEvent(KnownInstrumentationEvents.QuickAccessCustomPivotsImpression, (_a = {},
                _a["Distribution" /* InstrumentationParam.Distribution */] = distribution,
                _a));
            // TODO: Remove above Aria logging after fully migrating to OTEL
            logImpression({
                eventName: "QuickAccess_CustomPivots" /* KnownImpressionInstrumentationEvents.QuickAccessCustomPivotsImpression */,
                area: 'QuickAccess',
                distribution: distribution,
            });
        }, IDLE_VERYLOW_PRIORITY);
    }, [customPivots]);
    var commandFilterProps = useCommandFilter();
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    return canRender ? (React.createElement(React.Fragment, null,
        React.createElement(QuickAccessControl, { allDocs: documents, callbacks: quickAccessCallbacks, commandingEnabled: (_d = (_c = (_b = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _b === void 0 ? void 0 : _b.chatGates) === null || _c === void 0 ? void 0 : _c.chatEnabled) !== null && _d !== void 0 ? _d : false, dateResources: locStrings.dateResources, edgeworthItemResources: locStrings.edgeworthItemResources, edgeworthTaggingResources: locStrings.edgeworthTaggingResources, emptyStateResources: locStrings.emptyStateResources, identity: identity, isError: isError, isVisualRefresh: isVisualRefresh !== null && isVisualRefresh !== void 0 ? isVisualRefresh : false, layoutResources: locStrings.layoutResources, linksEnabled: (_f = (_e = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _e === void 0 ? void 0 : _e.edgeworthConfigGates) === null || _f === void 0 ? void 0 : _f.edgeworthConfigLinksEnabled, locResources: locStrings.edgeworthResources, preferredCulture: context.preferredCulture, maxItemCount: 12, navigateToMyContent: navigateToMyContent, uploadButton: uploadButton, getOrCreateUpnNameMap: getOrCreateUpnNameMap, fetchPictureData: fetchPictureData, customPivotsEnabled: isCustomPivotsEnabled, customPivotsProps: customPivots, deleteCustomPivot: deleteCustomPivot, fluidSupportEnabled: (capabilities === null || capabilities === void 0 ? void 0 : capabilities.fluidSupportEnabled) || false, gridViewEnabled: gridViewEnabled, currentLayout: currentLayout, darkMode: darkMode, PeopleCard: PeopleCard, workloadColorMap: workloadColorMap, cdnDomain: context.cdnDomain, tags: tags, msaUserHint: msaUserHint, onPivotRefChange: onPivotRefChange, isDesktopApp: context ? context.workload === OfficeWorkload.Hwa : false, commandFilterProps: {
                sharedByFilterText: commandFilterProps.sharedByFilterText,
                tagText: (_g = commandFilterProps.tagText) !== null && _g !== void 0 ? _g : '',
                fileTypeText: (_h = commandFilterProps.fileTypeText) !== null && _h !== void 0 ? _h : '',
                clearCommandFilterFileText: commandFilterProps.clearCommandFilterFileText,
                clearCommandFilterNameText: commandFilterProps.clearCommandFilterNameText,
                clearCommandFilterTagText: commandFilterProps.clearCommandFilterTagText,
            } }),
        showDialog ? (React.createElement("div", { role: "alert", "aria-label": dialogTitle, "aria-live": "assertive" },
            React.createElement(DialogControl, { title: dialogTitle || '', text: dialogText || '', onClose: function () {
                    resetV1UploadStatus();
                    setShowDialog(false);
                }, closeButtonAriaLabel: locStrings.confirmationDialogResources
                    .closeButtonLabel })) // Could add a progress bar here?
        ) : null, editLinkDialog !== null && editLinkDialog !== void 0 ? editLinkDialog : null)) : null;
};
//# sourceMappingURL=QuickAccessComponent.js.map